import React, { useState } from 'react';
import Toast from '../../components/common/Toast';
import { useLanguage } from '../../helpers/language-context';
import { renderHTML } from '../../helpers/render-html';
import { supabase } from '../../supabaseClient';
import { createClient } from '@supabase/supabase-js';

const ReportMisconduct = () => {
  const { translations } = useLanguage();
  const [formData, setFormData] = useState({
    staffName1: '',
    otherStaffNames: '',
    dateTime: '',
    location: '',
    incidentDetails: '',
    supportingDocument: null,
    whistleblowerName: '',
    whistleblowerEmail: '',
    contactNumber: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [fileName, setFileName] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        supportingDocument: file
      });
      setFileName(file.name);
    }
  };

  const handleSubmit = async () => {
    if (!formData.staffName1 || !formData.dateTime || !formData.location || !formData.incidentDetails || !formData.whistleblowerName || !formData.whistleblowerEmail) {
      setToast({ show: true, message: 'Please fill in all required fields.', type: 'error' });
      return;
    }

    setIsSubmitting(true);
    try {
      let documentUrl = '';

      if (formData.supportingDocument) {
        const file = formData.supportingDocument;
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('whistleblower_documents')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        // Get the public URL if the bucket is public, or we might store just the path if it's private
        // Assuming we store the path for now as it's likely a private bucket or we want to control access
        // But for simplicity in this codebase context, let's construct a path or URL.
        // If we want a full URL:
        // const { data: { publicUrl } } = supabase.storage.from('whistleblower_documents').getPublicUrl(filePath);
        // documentUrl = publicUrl;

        documentUrl = filePath; // Storing the path is safer for private buckets
      }

      const { error } = await supabase
        .from('whistleblower_reports')
        .insert([{
          staff_name_1: formData.staffName1,
          other_staff_name: formData.otherStaffNames,
          incident_date_time: formData.dateTime,
          incident_location: formData.location,
          incident_details: formData.incidentDetails,
          supporting_document_url: documentUrl,
          name: formData.whistleblowerName,
          email: formData.whistleblowerEmail,
          whistleblower_contact: formData.contactNumber,
          // Mapping old fields for backward compatibility if needed, or just relying on new columns
          message: formData.incidentDetails,
        }]);

      if (error) throw error;

      setToast({ show: true, message: 'Message sent successfully!', type: 'success' });
      setFormData({
        staffName1: '',
        otherStaffNames: '',
        dateTime: '',
        location: '',
        incidentDetails: '',
        supportingDocument: null,
        whistleblowerName: '',
        whistleblowerEmail: '',
        contactNumber: ''
      });
      setFileName('');

      // Send Email Notification via Brevo
      try {
        const apiKey = process.env.REACT_APP_BREVO_API_KEY;
        const listId = process.env.REACT_APP_BREVO_LIST_ID;
        const legacyRecipient = process.env.REACT_APP_WHISTLEBLOWER_RECIPIENT_EMAIL;

        console.log('Starting Brevo email process...', { listId, legacyRecipient });

        if (apiKey) {
          let recipients = [];

          // If List ID is provided, fetch contacts from that list
          if (listId) {
            try {
              console.log(`Fetching contacts from Brevo list: ${listId}`);
              const listResponse = await fetch(`https://api.brevo.com/v3/contacts/lists/${listId}/contacts?limit=50&offset=0`, {
                method: 'GET',
                headers: {
                  'accept': 'application/json',
                  'api-key': apiKey
                }
              });

              if (listResponse.ok) {
                const listData = await listResponse.json();
                console.log('Brevo list data received:', listData);
                if (listData.contacts && Array.isArray(listData.contacts) && listData.contacts.length > 0) {
                  recipients = listData.contacts.map(contact => ({ 
                    email: contact.email,
                    name: contact.email.split('@')[0] // Fallback name
                  }));
                  console.log(`Found ${recipients.length} recipients in list.`);
                } else {
                  console.warn('Brevo list is empty or has no contacts key.');
                }
              } else {
                const errorText = await listResponse.text();
                console.error(`Failed to fetch contacts from list. Status: ${listResponse.status}, Error: ${errorText}`);
              }
            } catch (listError) {
              console.error('Error fetching list contacts:', listError);
            }
          }

          // If no recipients found from list or fetching failed, use the legacy recipient email
          if (recipients.length === 0 && legacyRecipient) {
            console.log(`Falling back to legacy recipient: ${legacyRecipient}`);
            recipients.push({ email: legacyRecipient });
          }

          if (recipients.length > 0) {
            let fileLink = documentUrl;

            // Try to generate a signed URL if there is a document
            if (documentUrl) {
              try {
                // Use admin client if available to bypass RLS for signing
                let storageClient = supabase;
                const serviceRoleKey = process.env.REACT_APP_SUPABASE_SERVICE_ROLE_KEY;
                if (serviceRoleKey) {
                  console.log('Using service role key for signing URL');
                  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
                  if (supabaseUrl) {
                    storageClient = createClient(supabaseUrl, serviceRoleKey);
                  }
                }

                const { data, error } = await storageClient.storage
                  .from('whistleblower_documents')
                  .createSignedUrl(documentUrl, 60 * 60 * 24 * 7); // Valid for 7 days

                if (error) {
                  console.error('Supabase createSignedUrl error:', error);
                }

                if (data?.signedUrl) {
                  fileLink = data.signedUrl;
                } else {
                  const { data: publicData } = supabase.storage
                    .from('whistleblower_documents')
                    .getPublicUrl(documentUrl);
                  if (publicData?.publicUrl) fileLink = publicData.publicUrl;
                }
              } catch (urlError) {
                console.error('Error generating signed URL:', urlError);
                const { data: publicData } = supabase.storage
                  .from('whistleblower_documents')
                  .getPublicUrl(documentUrl);
                if (publicData?.publicUrl) fileLink = publicData.publicUrl;
              }
            }

            const emailContent = `
              <html>
                <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
                  <h2 style="color: #28357B;">New Speak Up Report Submitted</h2>
                  <p>A new whistleblowing report has been received through the KSSB website.</p>
                  <table style="width: 100%; border-collapse: collapse;">
                    <tr><td style="padding: 10px; border: 1px solid #eee; font-weight: bold; width: 200px;">Date & Time</td><td style="padding: 10px; border: 1px solid #eee;">${formData.dateTime}</td></tr>
                    <tr><td style="padding: 10px; border: 1px solid #eee; font-weight: bold;">Location</td><td style="padding: 10px; border: 1px solid #eee;">${formData.location}</td></tr>
                    <tr><td style="padding: 10px; border: 1px solid #eee; font-weight: bold;">Staff Involved</td><td style="padding: 10px; border: 1px solid #eee;">${formData.staffName1} ${formData.otherStaffNames ? `(${formData.otherStaffNames})` : ''}</td></tr>
                  </table>
                  <h3 style="color: #28357B; margin-top: 20px;">Incident Details:</h3>
                  <div style="background: #f9f9f9; padding: 15px; border-radius: 5px;">
                    ${formData.incidentDetails.replace(/\n/g, '<br>')}
                  </div>
                  <h3 style="color: #28357B; margin-top: 20px;">Reporter Details:</h3>
                  <p><strong>Name:</strong> ${formData.whistleblowerName}</p>
                  <p><strong>Email:</strong> ${formData.whistleblowerEmail}</p>
                  <p><strong>Contact:</strong> ${formData.contactNumber || 'Not provided'}</p>
                  ${(fileLink && fileLink.startsWith('http')) ? `<p style="margin-top: 20px;"><strong>Supporting Document:</strong> <a href="${fileLink}" style="color: #28357B; text-decoration: underline;">View Attachment</a><br/><small>(Link valid for 7 days)</small></p>` : ''}
                  <p style="margin-top: 30px; font-size: 12px; color: #777;">This is an automated notification from the KSSB Website.</p>
                </body>
              </html>
            `;

            console.log('Sending email invitation to:', recipients);
            const emailResponse = await fetch('https://api.brevo.com/v3/smtp/email', {
              method: 'POST',
              headers: {
                'accept': 'application/json',
                'api-key': apiKey,
                'content-type': 'application/json'
              },
              body: JSON.stringify({
                sender: { name: 'KSSB Website Speak Up', email: process.env.REACT_APP_BREVO_SENDER_EMAIL || 'admin@kssb.hadiantech.dev' },
                to: recipients,
                subject: 'URGENT: New Speak Up Report Submitted',
                htmlContent: emailContent
              })
            });

            if (emailResponse.ok) {
              console.log('Email sent successfully via Brevo');
            } else {
              const errorResult = await emailResponse.text();
              console.error('Brevo SMTP error:', errorResult);
            }
          } else {
            console.warn('No recipients found to send email to.');
          }
        } else {
          console.error('Brevo API key is missing in environment variables.');
        }
      } catch (emailError) {
        console.error('Outer Brevo process error:', emailError);
      }

    } catch (error) {
      console.error('Error submitting report:', error);
      setToast({ show: true, message: 'Failed to submit report. Please try again.', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="get-in-touch-section">
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
      <div className="w-full grid grid-cols-1 lg:grid-cols-2">
        {/* Left Column - Contact Info */}
        <div className="sub-section space-y-12 bg-gray-50 flex flex-col justify-start p-12">
          <div className="space-y-4">
            <h1 className="text-5xl lg:text-6xl text-[#28357B]" dangerouslySetInnerHTML={renderHTML(translations.governance.reportMisconduct)} />
            <p className="text-md text-slate-800">{translations.governance.reportMisconductDesc}</p>
          </div>

          <div className="space-y-6">
            {/* Email */}
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-[#FDA729] mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <div className="text-md text-gray-700">
                <p className="font-medium">{translations.governance.byEmail}</p>
                <a href="mailto:integriti@semesta.com.my" className="hover:text-[#28357B] underline">
                  integriti@semesta.com.my
                </a>
              </div>
            </div>

            {/* Address */}
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-[#FDA729] mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div className="text-md text-gray-700 space-y-2">
                <p className="font-medium">{translations.governance.byLetter}</p>
                <p className="whitespace-pre-line" dangerouslySetInnerHTML={renderHTML(translations.governance.letterRecipients)} />
                <p className="mt-4 whitespace-pre-line font-light" dangerouslySetInnerHTML={renderHTML(translations.governance.letterAddress)} />
              </div>
            </div>

            {/* e-Borang */}
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-[#FDA729] mt-1 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <div className="text-md text-gray-700">
                <a
                  href="http://semesta.com.my/governance#e-borang"
                  onClick={(e) => {
                    if (window.location.pathname.includes('/governance')) {
                      e.preventDefault();
                      document.getElementById('e-borang')?.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="font-medium hover:text-[#28357B] underline cursor-pointer"
                >
                  {translations.governance.eForm}
                </a>
              </div>
            </div>

            {/* Hotline */}
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-[#FDA729] mt-1 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <div className="text-md text-gray-700">
                <p className="font-medium">{translations.governance.hotline}</p>
                <a href="tel:+60129466692" className="hover:text-[#28357B] underline">
                  +6012-946 6692
                </a>
                <p className="text-sm text-gray-500 mt-1">{translations.governance.hotlineSub}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Form */}
        <div id="e-borang" className="sub-section p-12">
          <div className="space-y-12">

            {/* Incident Details Section */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-[#28357B]">{translations.governance.incidentDetails}</h3>

              {/* Staff Name 1 */}
              <div>
                <label htmlFor="staffName1" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {translations.governance.staffName1} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="staffName1"
                  name="staffName1"
                  value={formData.staffName1}
                  onChange={handleChange}
                  className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-indigo-600 focus:ring-0 bg-transparent transition-colors duration-200 outline-none placeholder-gray-400 font-medium"
                  placeholder="John Doe"
                />
              </div>

              {/* Other Staff Name */}
              <div>
                <label htmlFor="otherStaffNames" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {translations.governance.otherStaffNames}
                </label>
                <input
                  type="text"
                  id="otherStaffNames"
                  name="otherStaffNames"
                  value={formData.otherStaffNames}
                  onChange={handleChange}
                  className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-indigo-600 focus:ring-0 bg-transparent transition-colors duration-200 outline-none"
                />
              </div>

              {/* Date & Time */}
              <div>
                <label htmlFor="dateTime" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {translations.governance.dateTime} <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  id="dateTime"
                  name="dateTime"
                  value={formData.dateTime}
                  onChange={handleChange}
                  className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-indigo-600 focus:ring-0 bg-transparent transition-colors duration-200 outline-none"
                />
              </div>

              {/* Location */}
              <div>
                <label htmlFor="location" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {translations.governance.location} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-indigo-600 focus:ring-0 bg-transparent transition-colors duration-200 outline-none"
                />
              </div>

              {/* Incident Details */}
              <div>
                <label htmlFor="incidentDetails" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {translations.governance.incidentDetails} <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="incidentDetails"
                  name="incidentDetails"
                  value={formData.incidentDetails}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-indigo-600 focus:ring-0 bg-transparent transition-colors duration-200 outline-none resize-none"
                ></textarea>
              </div>

              {/* Supporting Document */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {translations.governance.supportingDocument}
                </label>
                <div className="relative">
                  <input
                    type="file"
                    id="supportingDocument"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="supportingDocument"
                    className="cursor-pointer inline-flex items-center px-6 py-2 border border-gray-300 rounded-full text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  >
                    {fileName || 'Upload'}
                    <svg className="ml-2 w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                    </svg>
                  </label>
                </div>
              </div>
            </div>

            {/* Whistleblower Details Section */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-[#28357B]">{translations.governance.whistleblowerDetails}</h3>

              {/* Name */}
              <div>
                <label htmlFor="whistleblowerName" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {translations.contact.name} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="whistleblowerName"
                  name="whistleblowerName"
                  value={formData.whistleblowerName}
                  onChange={handleChange}
                  className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-indigo-600 focus:ring-0 bg-transparent transition-colors duration-200 outline-none"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="whistleblowerEmail" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {translations.contact.email} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="whistleblowerEmail"
                  name="whistleblowerEmail"
                  value={formData.whistleblowerEmail}
                  onChange={handleChange}
                  className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-indigo-600 focus:ring-0 bg-transparent transition-colors duration-200 outline-none"
                />
              </div>

              {/* Contact Number */}
              <div>
                <label htmlFor="contactNumber" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {translations.governance.contactNumber}
                </label>
                <input
                  type="tel"
                  id="contactNumber"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleChange}
                  className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-indigo-600 focus:ring-0 bg-transparent transition-colors duration-200 outline-none"
                />
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="bg-[#28357B] hover:bg-indigo-800 text-white font-semibold px-8 py-3 rounded-full transition-colors duration-200 disabled:opacity-50"
                >
                  {isSubmitting ? 'Sending...' : translations.governance.sendMsg || 'Send Message'}
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

export default ReportMisconduct;
